import { ArrowLeft, Home, Square } from "lucide-react";
import type { Platform } from "../types";
import { TextSender } from "./TextSender";

interface Props {
  platform?: Platform;
  deviceWidth: number;
  deviceHeight: number;
  disabled?: boolean;
  onHome: () => void;
  onBack: () => void;
  onAppSwitcher: () => void;
  onType: (text: string) => void;
}

/**
 * Hardware-button panel below the screen. iOS has no back/recent_apps
 * keys — those are bottom-edge gestures, handled by callers constructing
 * a /drag request. Android uses the /press-key endpoint.
 */
export function DeviceControls({
  platform,
  disabled,
  onHome,
  onBack,
  onAppSwitcher,
  onType,
}: Props) {
  const isAndroid = platform === "android";
  const isIOS = platform === "ios";

  return (
    <div className="shrink-0 inline-flex items-center gap-1.5 mx-auto rounded-lg border border-surface-800 bg-surface-900/60 px-2 py-1.5">
      {/* Android keeps the legacy Back-Home-Recents order. iOS: just Home +
          app-switcher gesture (no Back). */}
      {isAndroid && (
        <button
          type="button"
          onClick={onBack}
          disabled={disabled}
          title="Back (KEYCODE_BACK)"
          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-surface-700 text-surface-200 hover:bg-surface-800 disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      )}
      <button
        type="button"
        onClick={onHome}
        disabled={disabled}
        title="Home"
        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-surface-700 text-surface-200 hover:bg-surface-800 disabled:opacity-50"
      >
        <Home className="w-4 h-4" />
      </button>
      {(isAndroid || isIOS) && (
        <button
          type="button"
          onClick={onAppSwitcher}
          disabled={disabled}
          title={
            isIOS
              ? "App switcher (swipe up + pause)"
              : "Recent apps (KEYCODE_APP_SWITCH)"
          }
          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-surface-700 text-surface-200 hover:bg-surface-800 disabled:opacity-50"
        >
          <Square className="w-4 h-4" />
        </button>
      )}
      <TextSender onSend={onType} disabled={disabled} />
    </div>
  );
}
